/**
 * Form Field Edit Dialog - New Implementation
 *
 * Edit form field properties.
 * Simplified without language dictionary - uses direct field values.
 */
import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	FormControlLabel,
	Switch,
	Box,
	Typography,
	MenuItem,
	IconButton,
	Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

const MIN_CHOICES = 2;

/**
 * FormFieldEditDialog - Modal for editing form field properties
 */
const FormFieldEditDialog = ({ open, field, onSave, onCancel }) => {
	const { messages } = useIntl();
	const [fieldName, setFieldName] = useState("");
	const [hintText, setHintText] = useState("");
	const [required, setRequired] = useState(false);
	const [fieldType, setFieldType] = useState("text");
	const [options, setOptions] = useState(["", ""]);
	const [draggedIndex, setDraggedIndex] = useState(null);

	// Initialize form with field data
	useEffect(() => {
		if (field) {
			setFieldName(field.fieldName || "");
			setHintText(field.hint || "");
			setRequired(field.required || false);
			setFieldType(field.fieldType || "text");
			// Initialize options for dropdown fields
			if (field.options && field.options.length >= MIN_CHOICES) {
				setOptions([...field.options]);
			} else {
				setOptions(["", ""]);
			}
		}
	}, [field]);

	// Helper to compare options arrays
	const optionsChanged = () => {
		const originalOptions = field?.options || [];
		if (options.length !== originalOptions.length) return true;
		return options.some((opt, i) => opt !== originalOptions[i]);
	};

	// Check if form has changes compared to original field
	const hasChanges = field && (
		fieldName !== (field.fieldName || "") ||
		hintText !== (field.hint || "") ||
		required !== (field.required || false) ||
		fieldType !== (field.fieldType || "text") ||
		(fieldType === "select" && optionsChanged())
	);

	// Validate: either fieldName or hintText must be present
	// For dropdown fields, also require at least MIN_CHOICES non-empty options
	const isValid = (() => {
		const hasNameOrHint = fieldName.trim() || hintText.trim();
		if (fieldType === "select") {
			const nonEmptyOptions = options.filter(opt => opt.trim());
			return hasNameOrHint && nonEmptyOptions.length >= MIN_CHOICES;
		}
		return hasNameOrHint;
	})();

	// Option management functions
	const handleOptionChange = (index, value) => {
		const newOptions = [...options];
		newOptions[index] = value;
		setOptions(newOptions);
	};

	const handleAddOption = (afterIndex) => {
		const newOptions = [...options];
		newOptions.splice(afterIndex + 1, 0, "");
		setOptions(newOptions);
	};

	const handleRemoveOption = (index) => {
		if (options.length <= MIN_CHOICES) return;
		const newOptions = options.filter((_, i) => i !== index);
		setOptions(newOptions);
	};

	// Drag and drop handlers
	const handleDragStart = (index) => {
		setDraggedIndex(index);
	};

	const handleDragOver = (e, index) => {
		e.preventDefault();
		if (draggedIndex === null || draggedIndex === index) return;

		const newOptions = [...options];
		const draggedItem = newOptions[draggedIndex];
		newOptions.splice(draggedIndex, 1);
		newOptions.splice(index, 0, draggedItem);
		setOptions(newOptions);
		setDraggedIndex(index);
	};

	const handleDragEnd = () => {
		setDraggedIndex(null);
	};

	const handleSave = () => {
		const saveData = {
			fieldName,
			hint: hintText,
			required,
			fieldType,
		};
		// Include options for dropdown fields (filter out empty ones)
		if (fieldType === "select") {
			saveData.options = options.filter(opt => opt.trim());
		}
		onSave(saveData);
	};

	return (
		<Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
			<DialogTitle>{messages.editFormField || "Edit Form Field"}</DialogTitle>
			<DialogContent>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
					<TextField
						label={messages.fieldName || "Field Name"}
						value={fieldName}
						onChange={(e) => setFieldName(e.target.value)}
						fullWidth
						helperText={messages.fieldNameHelperText || "The label shown to users (defaults to hint text if empty)"}
					/>

					<TextField
						label={messages.fieldType || "Field Type"}
						value={fieldType}
						onChange={(e) => setFieldType(e.target.value)}
						select
						fullWidth
						required
					>
						<MenuItem value="text">{messages.text || "Text"}</MenuItem>
						<MenuItem value="int">{messages.integer || "Integer"}</MenuItem>
						<MenuItem value="float">{messages.decimal || "Decimal"}</MenuItem>
						<MenuItem value="bool">{messages.checkbox || "Checkbox"}</MenuItem>
						<MenuItem value="select">{messages.dropdown || "Dropdown"}</MenuItem>
						<MenuItem value="photo">{messages.photo || "Photo"}</MenuItem>
					</TextField>

					<TextField
						label={messages.hintText || "Hint Text"}
						value={hintText}
						onChange={(e) => setHintText(e.target.value)}
						fullWidth
						multiline
						rows={2}
						helperText={messages.hintTextHelperText || "Optional help text shown to users"}
					/>

					{/* Choices section - only shown for dropdown field type */}
					{fieldType === "select" && (
						<Box sx={{ mt: 1 }}>
							<Typography variant="subtitle2" sx={{ mb: 1 }}>
								{messages.choices || "Choices"}
							</Typography>
							<Box
								sx={{
									maxHeight: 216,
									overflowY: "auto",
									border: "1px solid",
									borderColor: "divider",
									borderRadius: 1,
									p: 1,
								}}
							>
								{options.map((option, index) => (
									<Grid
										container
										key={index}
										alignItems="center"
										spacing={1}
										sx={{
											mb: 1,
											backgroundColor: draggedIndex === index ? "action.hover" : "transparent",
											borderRadius: 1,
										}}
										draggable
										onDragStart={() => handleDragStart(index)}
										onDragOver={(e) => handleDragOver(e, index)}
										onDragEnd={handleDragEnd}
									>
										<Grid item>
											<IconButton
												size="small"
												sx={{ cursor: "grab" }}
												disableRipple
											>
												<DragIndicatorIcon fontSize="small" />
											</IconButton>
										</Grid>
										<Grid item xs>
											<TextField
												value={option}
												onChange={(e) => handleOptionChange(index, e.target.value)}
												size="small"
												fullWidth
												placeholder={`${messages.option || "Option"} ${index + 1}`}
												inputProps={{ maxLength: 128 }}
											/>
										</Grid>
										<Grid item>
											<IconButton
												size="small"
												onClick={() => handleRemoveOption(index)}
												disabled={options.length <= MIN_CHOICES}
												color="error"
											>
												<DeleteOutlineIcon fontSize="small" />
											</IconButton>
										</Grid>
										<Grid item>
											<IconButton
												size="small"
												onClick={() => handleAddOption(index)}
												color="primary"
											>
												<AddIcon fontSize="small" />
											</IconButton>
										</Grid>
									</Grid>
								))}
							</Box>
							<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
								{messages.choicesHelperText || `Minimum ${MIN_CHOICES} choices required`}
							</Typography>
						</Box>
					)}

					<FormControlLabel
						control={
							<Switch
								checked={required}
								onChange={(e) => setRequired(e.target.checked)}
								color="error"
							/>
						}
						label={
							<Box>
								<Typography variant="body2">{messages.requiredField || "Required field"}</Typography>
								<Typography variant="caption" color="text.secondary">
									{messages.usersMustCompleteField || "Users must complete this field"}
								</Typography>
							</Box>
						}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onCancel}>{messages.cancel || "Cancel"}</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					disabled={!hasChanges || !isValid}
				>
					{messages.saveChanges || "Save Changes"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

FormFieldEditDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	field: PropTypes.shape({
		fieldId: PropTypes.string,
		fieldName: PropTypes.string,
		fieldType: PropTypes.string,
		hint: PropTypes.string,
		required: PropTypes.bool,
		options: PropTypes.arrayOf(PropTypes.string),
	}),
	onSave: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
};

export default FormFieldEditDialog;
